import React, { useState } from 'react';
import { AlertTriangle, AlertCircle, Sparkles, X, Check } from 'lucide-react';
import Carousel from './Carousel';
import './orchestration.css';

/**
 * MiloBriefing — ambient proactive Milo. The few things Milo wants the host to
 * see on arrival, surfaced before they have to ask. Deterministic content comes
 * from the backend /milo/briefing endpoint (risks · gaps · recommendations,
 * ranked by urgency). Every nudge routes through the same action handler the
 * recommendations use; each can be dismissed.
 *
 * Quiet mode: the host can mute Milo with the switch — he collapses to a slim,
 * friendly "resting" bar with a one-tap way back on, and never interrupts until
 * re-enabled. Nothing forces the host to hear from Milo.
 *
 * Multi-select: when `selectable` is true each nudge shows a leading selection
 * checkbox wired into the parent's shared notice selection (token `nudge:<key>`),
 * so nudges join the same bulk-action bar as tasks / gaps / since-changes. Each
 * nudge keeps its own X dismiss (persisted via the parent's dismissMiloNudge).
 *
 * Milo's branded avatar is loaded from /milo-avatar.png (drop the render in
 * frontend/public). Until it exists, a tasteful gold "M" badge stands in.
 */

const MILO_AVATAR = '/milo-avatar.png';

const SEV = {
  critical:    { cls: 'crit', Icon: AlertTriangle, label: 'Needs you' },
  warning:     { cls: 'warn', Icon: AlertCircle,   label: 'Watch' },
  opportunity: { cls: 'opp',  Icon: Sparkles,      label: 'Get ahead' },
};

export default function MiloBriefing({
  briefing, onAction, onDismiss, onSetQuiet, busy = false,
  selectable = false, isSelected, onToggleSelect,
}) {
  const [avatarOk, setAvatarOk] = useState(true);
  if (!briefing) return null;
  const { nudges = [], lead, all_clear: allClear, quiet } = briefing;

  const face = (small) => (avatarOk ? (
    <img className={`ero-milo-face${small ? ' sm' : ''}`} src={MILO_AVATAR} alt="Milo" onError={() => setAvatarOk(false)} />
  ) : (
    <div className={`ero-milo-face ero-milo-face--fallback${small ? ' sm' : ''}`} aria-hidden="true">M</div>
  ));

  // ── Quiet mode — Milo stays out of the way, with a gentle way back on. ──
  if (quiet) {
    return (
      <section className="ero-glass ero-accent ero-milo is-quiet ero-rise" style={{ animationDelay: '.12s' }} data-testid="milo-briefing">
        <div className="ero-milo-rest">
          <div className="ero-milo-avatar sm">{face(true)}</div>
          <span className="ero-milo-rest-text">Milo is resting — he won’t interrupt you.</span>
          <button type="button" className="ero-milo-switch is-action" role="switch" aria-checked="false"
                  disabled={busy} onClick={() => onSetQuiet?.(false)} title="Turn Milo back on">
            <span className="ero-milo-track"><span className="ero-milo-thumb" /></span>
            <span className="ero-milo-switch-lbl">Turn Milo on</span>
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`ero-glass ero-accent ero-milo ero-rise${allClear ? ' is-clear' : ''}`}
      style={{ animationDelay: '.12s' }}
      data-testid="milo-briefing"
    >
      <div className="ero-milo-head">
        <div className="ero-milo-avatar">
          {face(false)}
          <span className="ero-milo-pulse" aria-hidden="true" />
        </div>
        <div className="ero-milo-intro">
          <span className="ero-milo-attn"><b>{nudges.length}</b> thing{nudges.length !== 1 ? 's' : ''} that need your attention</span>
          {lead && <span className="ero-milo-lead">{lead}</span>}
        </div>
        <button type="button" className="ero-milo-switch" role="switch" aria-checked="true"
                disabled={busy} onClick={() => onSetQuiet?.(true)}
                title="Mute Milo — he’ll stay quiet until you turn him back on">
          <span className="ero-milo-switch-lbl on">On</span>
          <span className="ero-milo-track on"><span className="ero-milo-thumb" /></span>
        </button>
      </div>

      {!allClear && nudges.length > 0 && (
        <Carousel className="ero-milo-nudges" ariaLabel="Things that need your attention">
          {nudges.map((n) => {
            const s = SEV[n.severity] || SEV.opportunity;
            const Icon = s.Icon;
            const token = `nudge:${n.key}`;
            const sel = selectable && typeof isSelected === 'function' && isSelected(token);
            return (
              <div className={`ero-milo-nudge ${s.cls}${sel ? ' ero-selected' : ''}`} key={n.key}>
                {selectable && (
                  <button
                    type="button"
                    className={`ero-select${sel ? ' on' : ''}`}
                    disabled={busy}
                    role="checkbox"
                    aria-checked={sel}
                    aria-label={sel ? 'Deselect nudge' : 'Select nudge'}
                    onClick={() => onToggleSelect?.(token)}
                  >
                    {sel ? <Check size={13} aria-hidden="true" /> : null}
                  </button>
                )}
                <span className="ero-milo-sevic" aria-hidden="true"><Icon /></span>
                <div className="ero-milo-ntext">
                  <div className="ero-milo-ntop">
                    <span className="ero-milo-nhead">{n.headline}</span>
                    <span className={`ero-milo-sev ${s.cls}`}>{s.label}</span>
                  </div>
                  {n.detail && <div className="ero-milo-ndetail">{n.detail}</div>}
                  {n.why_now && <div className="ero-milo-nwhy">{n.why_now}</div>}
                  <div className="ero-milo-nacts">
                    {(n.actions || []).map((a, i) => (
                      <button key={i} type="button" className="ero-btn sm" disabled={busy}
                              onClick={() => onAction?.(a)}>{a.label}</button>
                    ))}
                    <button type="button" className="ero-btn sm ero-btn-ghost" disabled={busy}
                            title="Dismiss — Milo won’t bring this up again"
                            onClick={() => onDismiss?.(n.key)}>Dismiss</button>
                  </div>
                </div>
                <button type="button" className="ero-milo-x" title="Dismiss" aria-label="Dismiss nudge"
                        disabled={busy} onClick={() => onDismiss?.(n.key)}>
                  <X />
                </button>
              </div>
            );
          })}
        </Carousel>
      )}
    </section>
  );
}
